import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import programs from '../programs.json';
import {
    PHONE,
    PHONE_DISPLAY,
    EMAIL,
    CONSULTATION_FEE,
    formatPrice,
    whatsappLink,
    mailtoLink,
} from '../siteConfig';

const CONTACT_CARDS = [
    {
        key: 'call',
        icon: 'fa fa-phone',
        label: 'Call',
        value: PHONE_DISPLAY,
        href: `tel:${PHONE}`,
        note: 'Mon–Sat, 10am – 6pm',
    },
    {
        key: 'whatsapp',
        icon: 'fa fa-whatsapp',
        label: 'WhatsApp',
        value: PHONE_DISPLAY,
        href: whatsappLink('Hi Garima, I would like to know more about your nutrition programmes.'),
        note: 'Quickest way to reach us',
        external: true,
    },
    {
        key: 'email',
        icon: 'fa fa-envelope-o',
        label: 'Email',
        value: EMAIL,
        href: `mailto:${EMAIL}`,
        note: 'Replies within 1–2 days',
    },
];

const STEPS = [
    {
        icon: 'fa fa-comments',
        title: 'Get in touch',
        text: 'Send an enquiry with the programme you are interested in and a little about your goals.',
    },
    {
        icon: 'fa fa-user',
        title: 'Consultation',
        text: 'We go through your medical history, lifestyle, preferences and any allergies or intolerances.',
    },
    {
        icon: 'fa fa-check',
        title: 'Your plan',
        text: 'You receive a customised plan built around foods you already eat, with support as you settle into it.',
    },
];

const EMPTY_FORM = { name: '', phone: '', program: '', message: '' };

function Consultation() {
    const [form, setForm] = useState(EMPTY_FORM);

    const update = (field) => (event) =>
        setForm((current) => ({ ...current, [field]: event.target.value }));

    // The enquiry is handed to WhatsApp (or the mail client) prefilled — there
    // is no server behind this site to accept a POST.
    const buildMessage = () => {
        const lines = ['Hi Garima, I would like to book a consultation.'];
        if (form.name) lines.push(`Name: ${form.name}`);
        if (form.phone) lines.push(`Phone: ${form.phone}`);
        if (form.program) lines.push(`Programme: ${form.program}`);
        if (form.message) lines.push(`About me: ${form.message}`);
        return lines.join('\n');
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        window.open(whatsappLink(buildMessage()), '_blank', 'noopener,noreferrer');
    };

    return (
        <section className="consultation-area">
            <div className="consultation-bg">
                <img src={`${process.env.PUBLIC_URL}/images/pattern/consultation-bg.jpg`} alt="" aria-hidden="true"/>
            </div>
            <div className="container inner-content">
                <div className="row">
                    <div className="col-12">
                        <div className="sec-title">
                            <div className="title left">For Consultation</div>
                            <div className="dector thm-bg-clr"></div>
                        </div>
                    </div>
                </div>

                <div className="row align-items-center">
                    <div className="col-12 col-md-6">
                        <div className="consultation">
                            <div className="address">
                                <p>Nutrism not only supports a healthy way of eating for life but also an appetite to enjoy life to the full by reaping the benefits of optimum nutrition.</p>
                            </div>

                            <ul className="contact-cards">
                                {CONTACT_CARDS.map((card) => (
                                    <li key={card.key}>
                                        <a
                                            className="contact-card"
                                            href={card.href}
                                            {...(card.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                        >
                                            <span className="contact-card-icon">
                                                <i className={card.icon} aria-hidden="true"></i>
                                            </span>
                                            <span className="contact-card-body">
                                                <span className="contact-card-label">{card.label}</span>
                                                <span className="contact-card-value">{card.value}</span>
                                                <span className="contact-card-note">{card.note}</span>
                                            </span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div className="col-12 col-md-6 img-box">
                        <picture>
                            <source
                                type="image/webp"
                                srcSet={`${process.env.PUBLIC_URL}/images/resources/consultation-760.webp 760w, ${process.env.PUBLIC_URL}/images/resources/consultation-1140.webp 1140w`}
                                sizes="(max-width: 767px) 100vw, 505px"
                            />
                            <img
                                src={`${process.env.PUBLIC_URL}/images/resources/consultation-760.jpg`}
                                srcSet={`${process.env.PUBLIC_URL}/images/resources/consultation-760.jpg 760w, ${process.env.PUBLIC_URL}/images/resources/consultation-1140.jpg 1140w`}
                                sizes="(max-width: 767px) 100vw, 505px"
                                width="760"
                                height="1012"
                                alt="Garima Rathore"
                            />
                        </picture>
                    </div>
                </div>

                <div className="row">
                    <div className="col-12">
                        <div className="fee-callout">
                            <p className="consultation-fee consultation-fee--light">
                                <span className="consultation-fee-label">Consultation Fee</span>
                                <span className="consultation-fee-amount">&#8377; {formatPrice(CONSULTATION_FEE)}/-</span>
                            </p>
                            <NavLink to="/programs" className="fee-callout-link">
                                View all programmes &amp; pricing
                            </NavLink>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-12">
                        <h2 className="block-title">How a consultation works</h2>
                        <ol className="steps-list">
                            {STEPS.map((step, index) => (
                                <li key={step.title}>
                                    <span className="step-number">{index + 1}</span>
                                    <span className="step-body">
                                        <span className="step-title">
                                            <i className={step.icon} aria-hidden="true"></i>
                                            {step.title}
                                        </span>
                                        <span className="step-text">{step.text}</span>
                                    </span>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>

                <div className="row">
                    <div className="col-12 col-lg-8">
                        <h2 className="block-title">Send an enquiry</h2>
                        <p className="enquiry-intro">
                            Fill this in and it opens WhatsApp with your details ready to send — nothing is stored on this site.
                        </p>
                        <form className="enquiry-form" onSubmit={handleSubmit}>
                            <div className="enquiry-row">
                                <label className="enquiry-field">
                                    <span>Name</span>
                                    <input type="text" value={form.name} onChange={update('name')} autoComplete="name" required/>
                                </label>
                                <label className="enquiry-field">
                                    <span>Phone</span>
                                    <input type="tel" value={form.phone} onChange={update('phone')} autoComplete="tel"/>
                                </label>
                            </div>
                            <label className="enquiry-field">
                                <span>Programme</span>
                                <select value={form.program} onChange={update('program')}>
                                    <option value="">Not sure yet</option>
                                    {programs.map((program) => (
                                        <option key={program.name} value={program.name}>{program.name}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="enquiry-field">
                                <span>Anything you would like to share</span>
                                <textarea rows="4" value={form.message} onChange={update('message')}></textarea>
                            </label>
                            <div className="enquiry-actions">
                                <button type="submit" className="enquiry-submit">
                                    <i className="fa fa-whatsapp" aria-hidden="true"></i>
                                    Send on WhatsApp
                                </button>
                                <a
                                    className="enquiry-alt"
                                    href={mailtoLink('Consultation enquiry', buildMessage())}
                                >
                                    or send it by email
                                </a>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default Consultation
